import clsx from 'clsx';

import { Icon } from 'common/icon';

import { SlotInfoBlockParams } from 'constants/templates';

import { ISlot } from 'constants/types';

import { CSSProperties, useCallback, useMemo, useState } from 'react';

import { LogoOverlay, LogoOverlayWrapper } from 'components/logo-overlay';
import { SlotReviewModal } from 'components/modal/slot-review';
import { useProviderByIdQuery } from 'hooks/api/provider';

import { LoginModal } from 'components/modal/login';

import { BlurHashImage } from 'components/blurhash-image';
import { Routes } from 'constants/routes';
import { useLoginModal } from 'hooks';
import { getItemIdentifier } from 'utils/slug';

import styles from './styles.module.css';
import { Analytics } from 'constants/analytics';

export const Breadcrumbs = () => {
  return (
    <ul className={clsx(styles.slot_block__breadcrumbs, styles.breadcrumbs)}>
      <li className={styles.breadcrumbs__item}>
        <a href="#" className={styles.breadcrumbs__link}>
          <Icon.Home />
          <span>{'Home'}</span>
        </a>
      </li>
      <li className={styles.breadcrumbs__item}>
        <a href="#" className={styles.breadcrumbs__link}>
          {'Slots'}
        </a>
      </li>
      <li className={styles.breadcrumbs__item}>{'Colt Lightning'}</li>
    </ul>
  );
};

export const SlotInfoBlock = ({ data }: { data: ISlot }) => {
  const { data: provider = { name: 'Loading...' } } = useProviderByIdQuery({
    id: data?.provider_id,
  });

  const getMinMaxValues = useCallback(() => {
    if (data?.rtp_low && data?.rtp_hight) {
      if (data?.rtp_low === data?.rtp_hight) {
        return `${data?.rtp_low}%`;
      }
      return `${data?.rtp_low}-${data?.rtp_hight}%`;
    }
    if (data?.rtp_hight) {
      return `${data?.rtp_low}%`;
    }
    if (data?.rtp_low) {
      return `${data?.rtp_low}%`;
    }
    return '';
  }, [data?.rtp_hight, data?.rtp_low]);

  const items = useMemo(() => {
    const array: SlotInfoBlockParams[] = [];
    array.push({
      id: 1,
      text: 'Possible RTP',
      value: getMinMaxValues(),
    });
    array.push({
      id: Analytics.slot.providerPage,
      text: 'Provider',
      value: provider?.name ?? '',
      link: Routes.provider(getItemIdentifier(provider)),
    });
    array.push({
      id: 3,
      text: 'Volatility',
      value: `(${data?.volatility}/10)`,
    });
    array.push({
      id: 4,
      text: 'Max win',
      value: `${data?.maxwin}x`,
    });

    return array;
  }, [data, getMinMaxValues, provider]);

  return (
    <div className={styles.slot_block__data}>
      {items?.map((item) =>
        item?.link ? (
          <div
            id={item.id.toString()}
            key={item.id.toString()}
            className={styles.slot_block__data_item}
          >
            <span className={styles.itemText}>{item.text}:</span>
            <span>
              <a
                className={clsx(styles.itemText, styles.link)}
                href={item.link}
              >
                {item.value}
              </a>
            </span>
          </div>
        ) : (
          <div
            key={item.id.toString()}
            id={item.id.toString()}
            className={styles.slot_block__data_item}
          >
            <span className={styles.itemText}>{item.text}:</span>
            <span className={styles.itemText}>{item.value}</span>
          </div>
        )
      )}
    </div>
  );
};

export const Slot = ({ data }: { data: ISlot }) => {
  const { handleCloseLoginModal, handleConfirmLoginModal, isLoginVisible } =
    useLoginModal();

  const [isSlotReviewVisible, setSlotReviewVisible] = useState(false);

  const backgroundStyle: CSSProperties = {
    background: `url(${data?.imageUrl}) top/cover no-repeat`,
    opacity: '0.6999999881',
    WebkitFilter: 'blur(25px)',
    filter: 'blur(25px)',
    position: 'absolute',
    top: '0',
    left: '0',
    height: '410px',
    width: '100%',
    zIndex: '-1',
  };

  return (
    <section className={styles.slot_block}>
      <div style={backgroundStyle}></div>
      <div className={styles.container_s}>
        <div className={styles.slot_block__row}>
          <div className={styles.slot_block__card}>
            <div className={styles.slot_block__card_wrapper}>
              <LogoOverlayWrapper>
                <LogoOverlay alt={data?.name} image={data?.logoUrl} />

                <BlurHashImage
                  alt={data?.name}
                  src={data?.imageUrl ?? ''}
                  blurhash={data?.blurHash}
                  objectFit="cover"
                />
              </LogoOverlayWrapper>
            </div>
          </div>

          <div className={styles.slot_block__info}>
            <div className={styles.slot_block__title}>
              <h1>Slot {data?.name}</h1>
            </div>
            <SlotInfoBlock data={data} />
          </div>
        </div>

        <LoginModal
          isVisible={isLoginVisible}
          onClose={handleCloseLoginModal}
          confirmOnClick={handleConfirmLoginModal}
        />

        <SlotReviewModal
          slot={data}
          isVisible={isSlotReviewVisible}
          setVisible={setSlotReviewVisible}
        />
      </div>
    </section>
  );
};
